// ===== FILE: src/pages/Listing/ContractorCategoryLocationPage.tsx ===== (UPDATED)
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader } from 'reactstrap';
import { Link, useParams, useLoaderData } from 'react-router-dom';
import AutoBreadCrumb from '../../Components/Common/AutoBreadCrumb';
import SEOTags from './SEOTags';
import ContractorListingGrid from './ContractorListingGrid';
import ListingPagination from './ListingPagination';
import { useContractors } from '../../hooks/useContractors';
import { Contractor } from '../../models/Contractor.interface';
import { buildCategoryUrl, buildCityUrl, buildCategoryLocationUrl } from '../../utils/urlUtils';
import Header from "../Home/Header";
import Footer from "../Home/Footer";

interface CategoryLocationPageParams {
  state: string;
  city: string;
  category: string;
}

const ContractorCategoryLocationPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [stateDisplay, setStateDisplay] = useState('');
  const [cityDisplay, setCityDisplay] = useState('');
  const [categoryDisplay, setCategoryDisplay] = useState('');

  const params = useParams<{ state: string; city: string; categorySlug: string }>();
  const loaderData = useLoaderData() as CategoryLocationPageParams;
  const itemsPerPage = 12;

  // Get contractors matching all criteria (category + city + state)
  const {
    contractors,
    loading,
    error,
    pagination,
    setPage
  } = useContractors({
    category: loaderData.category,
    state: loaderData.state,
    city: loaderData.city,
    page: currentPage,
    limit: itemsPerPage
  }, true);

  // Convert slugs back to display names
  useEffect(() => {
    if (loaderData.state) {
      const stateFormatted = loaderData.state
        .replace(/-/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      setStateDisplay(stateFormatted);
    }

    if (loaderData.city) {
      const cityFormatted = loaderData.city
        .replace(/-/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      setCityDisplay(cityFormatted);
    }

    if (loaderData.category) {
      const categoryFormatted = loaderData.category
        .replace(/-/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      setCategoryDisplay(categoryFormatted);
    }
  }, [loaderData]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Get related categories from current contractors
  const getRelatedCategories = () => {
    const categoryCount: { [key: string]: number } = {};

    contractors.forEach(contractor => {
      if (contractor.categories) {
        contractor.categories.forEach(category => {
          // Don't include the current category
          if (category.toLowerCase() !== loaderData.category.toLowerCase()) {
            categoryCount[category] = (categoryCount[category] || 0) + 1;
          }
        });
      }
    });

    return Object.entries(categoryCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 8)
      .map(([category, count]) => ({ category, count }));
  };

  const relatedCategories = getRelatedCategories();

  return (
    <React.Fragment>
      <Header isFullWidth={true} />
      <div className="page-content">
        <SEOTags
          title={`${categoryDisplay} Contractors in ${cityDisplay}, ${stateDisplay}`}
          description={`Find ${categoryDisplay} contractors in ${cityDisplay}, ${stateDisplay}. Browse our directory of professional ${categoryDisplay} contractors serving ${cityDisplay} for your project needs.`}
          schemaType="ItemList"
        />

        <Container fluid>
          <AutoBreadCrumb />

          <Row>
            <Col lg={9}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    {categoryDisplay} Contractors in {cityDisplay}, {stateDisplay}
                    {!loading && (
                      <span className="text-muted ms-2">
                        ({pagination.totalCount} found)
                      </span>
                    )}
                  </h4>
                </CardHeader>

                <CardBody>
                  {error ? (
                    <div className="alert alert-danger">{error}</div>
                  ) : (
                    <>
                      {/* Search Results Summary */}
                      {!loading && pagination.totalCount > 0 && (
                        <div className="bg-light p-3 rounded mb-4">
                          <div className="row text-center">
                            <div className="col-md-3">
                              <h5 className="mb-1 text-primary">{pagination.totalCount}</h5>
                              <p className="text-muted mb-0">Local {categoryDisplay} Specialists</p>
                            </div>
                            <div className="col-md-3">
                              <h5 className="mb-1 text-primary">
                                {contractors.filter(c => c.rating && c.rating > 0).length}
                              </h5>
                              <p className="text-muted mb-0">Rated Contractors</p>
                            </div>
                            <div className="col-md-3">
                              <h5 className="mb-1 text-primary">
                                {(() => {
                                  const contractorsWithRatings = contractors.filter(c => c.rating && c.rating > 0);
                                  return contractorsWithRatings.length > 0
                                    ? (contractorsWithRatings.reduce((sum, c) => sum + (c.rating || 0), 0) / contractorsWithRatings.length).toFixed(1)
                                    : 'N/A';
                                })()}
                              </h5>
                              <p className="text-muted mb-0">Average Rating</p>
                            </div>
                            <div className="col-md-3">
                              <h5 className="mb-1 text-primary">
                                {contractors.filter(c => c.reviewCount && parseInt(c.reviewCount.toString()) > 0).length}
                              </h5>
                              <p className="text-muted mb-0">With Reviews</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <ContractorListingGrid
                        contractors={contractors}
                        loading={loading}
                      />

                      {contractors.length === 0 && !loading && (
                        <div className="text-center py-5">
                          <h5 className="text-muted mb-3">
                            No {categoryDisplay} contractors found in {cityDisplay}, {stateDisplay}
                          </h5>
                          <p className="text-muted mb-4">
                            Try expanding your search to nearby areas or browse related services.
                          </p>
                          <div className="d-flex flex-wrap justify-content-center gap-2">
                            <Link
                              to={buildCategoryUrl(loaderData.category)}
                              className="btn btn-outline-primary"
                            >
                              {categoryDisplay} in {stateDisplay}
                            </Link>
                            <Link
                              to={buildCityUrl(loaderData.state, loaderData.city)}
                              className="btn btn-outline-secondary"
                            >
                              All contractors in {cityDisplay}
                            </Link>
                          </div>
                        </div>
                      )}

                      {pagination.totalPages > 1 && (
                        <ListingPagination
                          currentPage={pagination.currentPage}
                          totalPages={pagination.totalPages}
                          onPageChange={handlePageChange}
                        />
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col lg={3}>
              {/* Related Categories */}
              {relatedCategories.length > 0 && (
                <Card className="mb-4">
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      Related Services in {cityDisplay}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <div className="list-group list-group-flush">
                      {relatedCategories.map((item) => (
                        <Link
                          key={item.category}
                          to={buildCategoryLocationUrl(loaderData.state, loaderData.city, item.category)}
                          className="list-group-item list-group-item-action d-flex justify-content-between align-items-center border-0 px-0"
                        >
                          <span className="fw-medium">{item.category}</span>
                          <span className="badge bg-light text-dark">
                            {item.count}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* Quick Navigation */}
              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">Expand Your Search</h5>
                </CardHeader>
                <CardBody>
                  <div className="d-grid gap-2">
                    <Link
                      to={buildCategoryUrl(loaderData.category)}
                      className="btn btn-outline-primary"
                    >
                      {categoryDisplay} in all {stateDisplay}
                    </Link>
                    <Link
                      to={buildCityUrl(loaderData.state, loaderData.city)}
                      className="btn btn-outline-secondary"
                    >
                      All contractors in {cityDisplay}
                    </Link>
                    <Link
                      to={buildCategoryUrl(loaderData.category)}
                      className="btn btn-outline-info"
                    >
                      {categoryDisplay} nationwide
                    </Link>
                  </div>
                </CardBody>
              </Card>

              {/* Search Tips */}
              <Card>
                <CardHeader>
                  <h5 className="card-title mb-0">Search Tips</h5>
                </CardHeader>
                <CardBody>
                  <ul className="list-unstyled mb-0">
                    <li className="mb-2">
                      <i className="bi bi-check-circle text-success me-2"></i>
                      Check contractor ratings and reviews
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle text-success me-2"></i>
                      Verify licensing and insurance
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle text-success me-2"></i>
                      Get multiple quotes for comparison
                    </li>
                    <li className="mb-0">
                      <i className="bi bi-check-circle text-success me-2"></i>
                      Ask for references from recent projects
                    </li>
                  </ul>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default ContractorCategoryLocationPage;